import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import ResizeHandle from "./ResizeHandle";
import SpeechBubble from "./SpeechBubble";
import SFXText from "./SFXText";
import ScreentoneOverlay from "./ScreentoneOverlay";
import FrameMatte from "./FrameMatte";
import { findPanelShape } from "../constants/panelShapes";
import { findComicStyle } from "../constants/comicStyles";
import { polygonClipPath } from "../utils/panelGeometry";
import { dragHasFiles, imageFromDataTransfer } from "../utils/imageImport";
import { usePointerDrag } from "../hooks/usePointerDrag";
import { useUndoGesture } from "../hooks/useUndoGesture";
import { useImageSize, artPlacement } from "../hooks/useImageSize";
import { useEditorStore, useEditorStoreApi } from "../store/editorStoreSelectors";

const Cell = ({
  cell,
  onStartDragEdge,
  onUpdateSpeechBubble,
  onUpdateItem,
  onUpdateSFX,
  onDuplicateBubble,
  onDuplicateSFX,
  onLetterAt,
  onDropImage,
}) => {
  const edges = ["top", "bottom", "left", "right"];
  const corners = ["nw", "ne", "sw", "se"];
  const [isHovered, setIsHovered] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);

  // Read selection state from the store
  const selection = useEditorStore((s) => s.selection);
  const multiSelect = useEditorStore((s) => s.multiSelect);
  const showReadingOrder = useEditorStore((s) => s.showReadingOrder);
  const readingDirection = useEditorStore((s) => s.readingDirection);
  const setSelection = useEditorStore((s) => s.setSelection);
  const setMultiSelect = useEditorStore((s) => s.setMultiSelect);
  const setContextMenu = useEditorStore((s) => s.setContextMenu);
  const comicStyleKey = useEditorStore((s) => s.comicStyleKey);
  const comicStyle = findComicStyle(comicStyleKey);
  // Storyboard frames are generated at a locked aspect: no edge resizing
  const storyboard = useEditorStore((s) => s.storyboard);

  const isSelected = selection?.type === "cell" && selection.cellId === cell.id;
  const isMultiSelected = multiSelect.includes(cell.id);
  const selectedBubbleId = selection?.type === "bubble" && selection.cellId === cell.id
    ? selection.bubbleId : null;
  const selectedSFXId = selection?.type === "sfx" && selection.cellId === cell.id
    ? selection.sfxId : null;

  // Compute reading order index — only subscribe to cells when reading order is visible
  const cells = useEditorStore((s) => showReadingOrder ? s.cells : null);
  const readingOrderIndex = useMemo(() => {
    if (!showReadingOrder || !cells) return null;
    const rowThreshold = 20;
    const sorted = [...cells].sort((a, b) => {
      const rowDiff = a.y - b.y;
      if (Math.abs(rowDiff) > rowThreshold) return rowDiff;
      return readingDirection === "rtl" ? b.x - a.x : a.x - b.x;
    });
    return sorted.findIndex((c) => c.id === cell.id);
  }, [cells, showReadingOrder, readingDirection, cell.id]);

  const [activeGuides, setActiveGuides] = useState([]);

  const snapGridSize = useEditorStore((s) => s.snapGridSize);
  const snapEnabled = useEditorStore((s) => s.snapEnabled);
  const showSnapGrid = useEditorStore((s) => s.showSnapGrid);

  const items = cell.items || [];
  const backgroundItem = items.find((item) => item.type === "background");
  const speechBubbles = items.filter((item) => item.type === "speechBubble");
  const sfxItems = items.filter((item) => item.type === "sfx");
  const contentItems = items.filter((item) => item.type === "speechBubble" || item.type === "sfx");

  // Compute all item rects for sibling snap alignment
  const allItemRects = useMemo(() => {
    const rects = [];
    for (const b of speechBubbles) {
      rects.push({ id: b.id, x: b.x ?? 16, y: b.y ?? 16, width: b.width ?? 160, height: b.height ?? 96 });
    }
    for (const s of sfxItems) {
      const sfxFontSize = s.fontSize || 64;
      const sfxText = s.text || "BOOM";
      const estWidth = sfxText.length * sfxFontSize * 0.6 * (s.scaleX ?? 1);
      const estHeight = sfxFontSize * 1.2 * (s.scaleY ?? 1);
      rects.push({ id: s.id, x: s.x ?? 20, y: s.y ?? 20, width: estWidth, height: estHeight });
    }
    return rects;
  }, [speechBubbles, sfxItems]);

  const getSiblingRects = useCallback((excludeId) => {
    return allItemRects.filter((r) => r.id !== excludeId);
  }, [allItemRects]);

  const handleSelectCell = (event) => {
    if (event?.shiftKey) {
      setMultiSelect(
        multiSelect.includes(cell.id)
          ? multiSelect.filter((id) => id !== cell.id)
          : [...multiSelect, cell.id]
      );
    } else {
      setMultiSelect([]);
      setSelection({ type: "cell", cellId: cell.id });
    }
  };

  // Panels from an angled knife cut are polygons inside their rect. CSS
  // borders and outlines can't follow a clip-path, so those panels draw
  // their border and selection ring as SVG instead.
  const polygon = cell.polygon || null;
  const borderWidth = cell.borderWidth ?? comicStyle.panel.borderWidth;
  const borderStyle = cell.borderStyle || comicStyle.panel.borderStyle;
  const borderColor = cell.borderColor || comicStyle.panel.borderColor;
  const polygonPoints = polygon ? polygon.map(([px, py]) => `${px},${py}`).join(" ") : null;
  const ringColor = isMultiSelected || isSelected ? "#00a4e4" : "rgba(0,164,228,0.55)";
  const showRing = isMultiSelected || isSelected || isHovered;

  // Panel-local position of a pointer event (the page may be zoomed)
  const toLocal = (event) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    const zoom = rect.width / (el.offsetWidth || 1) || 1;
    return {
      x: (event.clientX - rect.left) / zoom - el.clientLeft,
      y: (event.clientY - rect.top) / zoom - el.clientTop,
    };
  };

  const handleDoubleClick = (event) => {
    // Only empty paper: bubbles, SFX, and handles have their own double-click
    if (!onLetterAt || event.target.closest("[data-canvas-item]")) return;
    if (event.target !== event.currentTarget && !event.target.closest("[data-panel-surface]")) return;
    event.stopPropagation();
    onLetterAt(cell.id, toLocal(event));
  };

  // ─── Art: drag to slide it inside the frame, scroll to scale it ────────
  // Only once the panel is selected, so selecting never nudges the art.
  // Art covers the panel's window inside its border (same as the renderer)
  const artInset = cell.polygon ? 0 : (cell.borderWidth ?? comicStyle.panel.borderWidth ?? 0);
  const artBoxW = Math.max(1, cell.width - artInset * 2);
  const artBoxH = Math.max(1, cell.height - artInset * 2);
  const artSize = useImageSize(backgroundItem?.url);
  const art = backgroundItem && artSize
    ? artPlacement(artBoxW, artBoxH, artSize, backgroundItem.zoom || 100, backgroundItem.panX || 0, backgroundItem.panY || 0)
    : null;
  const canMoveArt = Boolean(backgroundItem && onUpdateItem && isSelected);
  const artDrag = usePointerDrag({
    onMove: ({ dx, dy, payload }) => {
      const clampPan = (value, max) => (max == null ? value : Math.max(-max, Math.min(max, value)));
      onUpdateItem(cell.id, payload.id, {
        panX: clampPan(payload.panX + (dx / artBoxW) * 100, payload.maxPanX),
        panY: clampPan(payload.panY + (dy / artBoxH) * 100, payload.maxPanY),
      });
    },
  });
  useUndoGesture(Boolean(artDrag.active));

  const handleArtPointerDown = (event) => {
    if (!canMoveArt || event.defaultPrevented) return;
    if (event.target !== event.currentTarget && !event.target.closest("[data-panel-surface]")) return;
    if (event.target.closest("[data-canvas-item]")) return;
    // The art can slide until it would uncover the frame
    artDrag.start(event, {
      id: backgroundItem.id,
      panX: backgroundItem.panX || 0,
      panY: backgroundItem.panY || 0,
      maxPanX: art ? art.maxPanX : null,
      maxPanY: art ? art.maxPanY : null,
    });
  };

  const storeApi = useEditorStoreApi();
  const cellRef = useRef(null);
  const wheelStateRef = useRef(null);
  wheelStateRef.current = { canMoveArt, backgroundItem, onUpdateItem, cellId: cell.id };
  useEffect(() => {
    const el = cellRef.current;
    if (!el) return undefined;
    // Native, non-passive: React's onWheel can't stop the desk scrolling too
    // A burst of wheel ticks is one undo step: the gesture closes after a pause
    let idleTimer = null;
    const handleWheel = (event) => {
      const st = wheelStateRef.current;
      if (!st.canMoveArt || event.ctrlKey || event.metaKey) return;
      event.preventDefault();
      storeApi.getState().beginGesture();
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => storeApi.getState().endGesture(), 400);
      const delta = event.deltaY > 0 ? -5 : 5;
      const newZoom = Math.max(50, Math.min(300, (st.backgroundItem.zoom || 100) + delta));
      st.onUpdateItem(st.cellId, st.backgroundItem.id, { zoom: newZoom });
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", handleWheel);
      if (idleTimer) {
        window.clearTimeout(idleTimer);
        storeApi.getState().endGesture();
      }
    };
  }, [storeApi]);

  const dropHandlers = onDropImage ? {
    onDragEnter: (event) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      setIsDropTarget(true);
    },
    onDragOver: (event) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      if (!isDropTarget) setIsDropTarget(true);
    },
    onDragLeave: (event) => {
      // Ignore leaving into our own children
      if (event.currentTarget.contains(event.relatedTarget)) return;
      setIsDropTarget(false);
    },
    onDrop: (event) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      setIsDropTarget(false);
      const file = imageFromDataTransfer(event.dataTransfer);
      if (file) onDropImage(cell.id, file);
    },
  } : {};

  const cellStyle = {
    position: "absolute",
    left: cell.x,
    top: cell.y,
    width: cell.width,
    height: cell.height,
    zIndex: cell.zIndex || 0,
    border: polygon ? "none" : `${borderWidth}px ${borderStyle} ${borderColor}`,
    // Art is drawn by its own layer below (cover-fit, like the exports)
    backgroundColor: backgroundItem ? "#ffffff" : comicStyle.panel.emptyFill,
    clipPath: polygon
      ? polygonClipPath(polygon)
      : cell.panelShape ? findPanelShape(cell.panelShape).clipPath || undefined : undefined,
    boxSizing: "border-box",
    overflow: "hidden",
    cursor: canMoveArt ? (artDrag.active ? "grabbing" : "move") : undefined,
    touchAction: canMoveArt ? "none" : undefined,
    outline: polygon ? undefined : isMultiSelected ? "2px dashed #00a4e4" : isSelected ? "2px solid #00a4e4" : undefined,
    outlineOffset: !polygon && (isMultiSelected || isSelected) ? 2 : undefined,
    boxShadow: polygon ? undefined : isMultiSelected
      ? "0 0 0 5px rgba(0,164,228,0.22)"
      : isSelected
        ? "0 0 0 5px rgba(0,164,228,0.18)"
        : isHovered
          ? "0 0 0 3px rgba(0,164,228,0.28)"
          : undefined,
  };

  return (
    <>
    <div
      data-testid="layout-cell"
      data-cell-id={cell.id}
      className="striply-panel"
      style={cellStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={handleSelectCell}
      onClick={handleSelectCell}
      onContextMenu={(e) => {
        e.preventDefault();
        handleSelectCell(e);
        setContextMenu({ cellId: cell.id, x: e.clientX, y: e.clientY });
      }}
      onDoubleClick={handleDoubleClick}
      {...dropHandlers}
      ref={cellRef}
      onPointerDown={handleArtPointerDown}
      tabIndex={0}
    >
      {backgroundItem && (
        // Same maths as the PNG export (artPlacement). Until the image's size
        // is known, show it cover-fit and centred.
        <div
          aria-hidden="true"
          data-panel-art="1"
          style={{
            position: "absolute",
            zIndex: 0,
            pointerEvents: "none",
            backgroundImage: `url(${backgroundItem.url})`,
            backgroundRepeat: "no-repeat",
            ...(art
              ? { left: art.left, top: art.top, width: art.width, height: art.height, backgroundSize: "100% 100%" }
              : { inset: 0, backgroundSize: "cover", backgroundPosition: "center" }),
          }}
        />
      )}
      {showReadingOrder && readingOrderIndex != null && readingOrderIndex >= 0 && (
        <div
          style={{
            position: "absolute", top: 4, left: 4, zIndex: 20,
            width: 24, height: 24, borderRadius: 0,
            background: "#1a1a1a", color: "#ffe600",
            fontSize: 11, fontWeight: 700, fontFamily: "'Georgia', serif",
            display: "flex", alignItems: "center", justifyContent: "center",
            pointerEvents: "none", opacity: 0.85,
          }}
        >
          {readingOrderIndex + 1}
        </div>
      )}

      {/* Pencil hint on empty paper (editor only — never exported) */}
      {isHovered && items.length === 0 && !isDropTarget && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 8, textAlign: "center",
            fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: 12, lineHeight: 1.5,
            color: "rgba(0, 140, 196, 0.55)",
          }}
        >
          double-click to letter
          <br />
          drop or paste art
        </div>
      )}

      {isDropTarget && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0, zIndex: 40, pointerEvents: "none",
            background: "rgba(164, 221, 237, 0.45)",
            boxShadow: "inset 0 0 0 3px #00a4e4",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <span
            style={{
              padding: "4px 10px", background: "#fffdf6", border: "1px solid #1a1a1a",
              boxShadow: "2px 2px 0 rgba(0,0,0,0.2)", fontFamily: "'Georgia', serif",
              fontSize: 13, color: "#1a1a1a",
            }}
          >
            Drop art here
          </span>
        </div>
      )}

      {cell.screentone && cell.screentone !== "none" && (
        <ScreentoneOverlay
          patternKey={cell.screentone}
          width={cell.width}
          height={cell.height}
          opacity={cell.screentoneOpacity ?? 0.3}
          color={cell.screentoneColor || "#000000"}
        />
      )}

      {/* Render content items (bubbles & SFX) in array order for layer control */}
      {contentItems.map((item, index) => {
        if (item.type === "speechBubble") {
          return (
            <React.Fragment key={item.id}>
              <div data-panel-surface="1" style={{ position: "absolute", inset: 0, pointerEvents: "auto", zIndex: index + 1 }}>
                <SpeechBubble
                  bubble={{
                    shape: "rounded", tailEnabled: true,
                    x: 16, y: 16,
                    width: Math.max(160, Math.min(220, cell.width * 0.6)),
                    height: 96, tailWidth: 20,
                    ...item,
                  }}
                  comicStyle={comicStyle}
                  cellSize={{ width: cell.width, height: cell.height }}
                  onChange={(updates) => onUpdateSpeechBubble(cell.id, item.id, updates)}
                  onSelect={() => setSelection({ type: "bubble", cellId: cell.id, bubbleId: item.id })}
                  isSelected={selectedBubbleId === item.id}
                  siblingRects={getSiblingRects(item.id)}
                  snapGridSize={snapGridSize}
                  snapEnabled={snapEnabled}
                  onGuidesChange={setActiveGuides}
                  onDuplicate={() => onDuplicateBubble && onDuplicateBubble(cell.id, item.id)}
                />
              </div>
              {item.locked && (
                <div
                  style={{
                    position: "absolute",
                    left: (item.x ?? 16) + (item.width ?? 160) - 8,
                    top: (item.y ?? 16) - 4,
                    width: 20, height: 20, borderRadius: 0,
                    background: "#ffe600", border: "1px solid #1a1a1a", boxSizing: "border-box",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    pointerEvents: "none", zIndex: index + 1,
                  }}
                >
                  <span className="material-icons" style={{ fontSize: 12, color: "#1a1a1a" }}>lock</span>
                </div>
              )}
            </React.Fragment>
          );
        }
        if (item.type === "sfx") {
          return (
            <React.Fragment key={item.id}>
              <div style={{ position: "relative", zIndex: index + 1 }}>
                <SFXText
                  sfx={item}
                  comicStyle={comicStyle}
                  cellSize={{ width: cell.width, height: cell.height }}
                  onChange={(updates) => onUpdateSFX && onUpdateSFX(cell.id, item.id, updates)}
                  onSelect={() => setSelection({ type: "sfx", cellId: cell.id, sfxId: item.id })}
                  isSelected={selectedSFXId === item.id}
                  siblingRects={getSiblingRects(item.id)}
                  snapGridSize={snapGridSize}
                  snapEnabled={snapEnabled}
                  onGuidesChange={setActiveGuides}
                  onDuplicate={() => onDuplicateSFX && onDuplicateSFX(cell.id, item.id)}
                />
              </div>
              {item.locked && (
                <div
                  style={{
                    position: "absolute",
                    left: (item.x ?? 20) - 4,
                    top: (item.y ?? 20) - 4,
                    width: 20, height: 20, borderRadius: 0,
                    background: "#ffe600", border: "1px solid #1a1a1a", boxSizing: "border-box",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    pointerEvents: "none", zIndex: index + 1,
                  }}
                >
                  <span className="material-icons" style={{ fontSize: 12, color: "#1a1a1a" }}>lock</span>
                </div>
              )}
            </React.Fragment>
          );
        }
        return null;
      })}

      {storyboard && (
        <FrameMatte width={artBoxW} height={artBoxH} storyboard={storyboard} />
      )}

      {/* Snap grid overlay */}
      {showSnapGrid && snapGridSize > 0 && (
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <defs>
            <pattern
              id={`grid-${cell.id}`}
              width={snapGridSize}
              height={snapGridSize}
              patternUnits="userSpaceOnUse"
            >
              {/* Non-photo blue: the pencil-guide color that doesn't reproduce in print */}
              <path
                d={`M ${snapGridSize} 0 L 0 0 0 ${snapGridSize}`}
                fill="none"
                stroke="rgba(164,221,237,0.55)"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#grid-${cell.id})`} />
        </svg>
      )}

      {/* Snap alignment guides */}
      {activeGuides.map((guide, i) => (
        <div
          key={`guide-${guide.axis}-${guide.position}-${i}`}
          className="striply-guide"
          style={{
            position: "absolute",
            pointerEvents: "none",
            zIndex: 50,
            ...(guide.axis === "x"
              ? { left: guide.position, top: 0, width: 1, height: "100%", borderLeft: "1px dashed #00a4e4" }
              : { left: 0, top: guide.position, width: "100%", height: 1, borderTop: "1px dashed #00a4e4" }),
          }}
        />
      ))}

      {polygon && (
        <svg
          aria-hidden="true"
          width={cell.width}
          height={cell.height}
          style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", zIndex: 30, overflow: "visible" }}
        >
          {/* Double width: the clip-path trims the outer half, leaving an
              inside border exactly borderWidth wide, like the CSS border */}
          <polygon
            points={polygonPoints}
            fill="none"
            stroke={borderColor}
            strokeWidth={borderWidth * 2}
            strokeLinejoin="miter"
            strokeDasharray={borderStyle === "dashed" ? `${borderWidth * 4} ${borderWidth * 3}` : borderStyle === "dotted" ? `${borderWidth} ${borderWidth * 2}` : undefined}
          />
        </svg>
      )}

      {/* Angled panels resize by their gutters, not their bounding box */}
      {!polygon && !storyboard && edges.map((edge) => (
        <ResizeHandle
          key={edge}
          edge={edge}
          onPointerDown={(e) => onStartDragEdge(cell.id, edge, e)}
        />
      ))}

      {!polygon && !storyboard && corners.map((corner) => (
        <ResizeHandle
          key={corner}
          edge={corner}
          onPointerDown={(e) => onStartDragEdge(cell.id, corner, e)}
        />
      ))}
    </div>

    {polygon && showRing && (
      <svg
        aria-hidden="true"
        className="striply-panel"
        width={cell.width}
        height={cell.height}
        style={{
          position: "absolute", left: cell.x, top: cell.y, overflow: "visible",
          pointerEvents: "none", zIndex: (cell.zIndex || 0) + 1,
        }}
      >
        <polygon points={polygonPoints} fill="none" stroke="rgba(0,164,228,0.2)" strokeWidth={10} strokeLinejoin="round" />
        <polygon
          points={polygonPoints}
          fill="none"
          stroke={ringColor}
          strokeWidth={2}
          strokeDasharray={isMultiSelected ? "6 4" : undefined}
        />
      </svg>
    )}
    </>
  );
};

export default Cell;
